/** @file Paint-weights pointer behaviour: strokes, Alt bone pick, heat dots and brush ring, overlay heatmap. */
import { viewCamera } from '@pose2d/editor-geometry/viewCamera';
import type { Id } from '@pose2d/format/types';
import { viridis } from '@pose2d/overlay/draw/viridis';
import type { OverlayOptions } from '@pose2d/overlay/types';
import type { GizmoDrawing, ToolContext, ToolGesture, ViewportPointer } from '../../panels/viewport/viewportTypes';
import type { PendingCommand } from '../../stores/types';
import type { MeshToolPointer } from '../editMesh/editMeshPointer';
import { meshGizmo } from '../editMesh/meshGizmo';
import { meshScene, type MeshScene } from '../editMesh/meshScene';
import { pendingOf } from '../editMesh/pendingOf';
import { staleBind } from '../editMesh/staleBind';
import { brushCursor } from './brushCursor';
import { brushRing } from './brushRing';
import { brushStore } from './brushStore';
import { paintStroke, type PaintStroke } from './paintStroke';
import { staleNotice } from './staleNotice';
import { weightRows } from './weightRows';

type Influences = [Id, number][];

/** World point under the pointer and the brush radius in world units. */
function brushAt(s: MeshScene, p: ViewportPointer): { x: number; y: number; radius: number } {
  const [x, y] = viewCamera.screenToWorld(s.ctx.camera, p.sx, p.sy, [0, 0]);
  return { x, y, radius: brushStore.getState().radiusPx * viewCamera.worldPerPixel(s.ctx.camera) };
}

/** Wire, one dot per vertex coloured by its share of `bone` (viridis), and the ring at the brush cursor. */
function heatGizmo(s: MeshScene, rows: readonly Influences[], bone: Id): GizmoDrawing {
  const share = (v: number): number => rows[v]?.find(([b]) => b === bone)?.[1] ?? 0;
  const at = brushCursor.at;
  const ring = at === null ? [] : brushRing(at[0], at[1], brushStore.getState().radiusPx);
  return meshGizmo(s.ctx.camera, s.points, s.target.mesh.triangles, (v) => viridis(share(v), 255), ring);
}

/**
 * A running stroke: dabs along every move, the heat preview, one mesh.setWeights on release when anything changed. In
 * Setup mode a stale bind is re-captured first in the same batch, so the vertices stay where the dots are (T-473).
 */
function strokeGesture(s: MeshScene, stroke: PaintStroke): ToolGesture {
  s.ctx.setGizmo(heatGizmo(s, stroke.rows, stroke.bone));
  const commit = (): PendingCommand[] => pendingOf(staleBind.batch(s.ctx.doc, s.target, [weightRows.call(stroke.attachment, stroke.rows)], s.ctx.tool.mode));
  return {
    move(p) {
      brushCursor.at = [p.sx, p.sy];
      const [x, y] = viewCamera.screenToWorld(s.ctx.camera, p.sx, p.sy, [0, 0]);
      paintStroke.extend(stroke, x, y, brushStore.getState());
      s.ctx.setGizmo(heatGizmo(s, stroke.rows, stroke.bone));
    },
    release: () => (paintStroke.changed(stroke) ? commit() : []),
    cancel: () => undefined,
  };
}

/** A stroke's first dab at `at`; Animate mode never rebinds (the pose is animated on purpose) and says so once per mesh. */
function startStroke(s: MeshScene, rows: Influences[], at: { x: number; y: number; radius: number }): ToolGesture {
  if (s.ctx.tool.mode === 'animate') staleNotice.stroke(s.ctx, s.target.attachment, staleBind.bones(s.ctx.doc, s.target.mesh).length > 0);
  const brush = brushStore.getState();
  return strokeGesture(s, paintStroke.begin(s, rows, paintStroke.bone(s.ctx.doc, s.target, brush.bone), brush, at));
}

/** The paint bone of the selected mesh, or null without one. */
function paintBoneOf(s: MeshScene | null): Id | null {
  return s === null ? null : paintStroke.bone(s.ctx.doc, s.target, brushStore.getState().bone);
}

/** Press: Alt picks the heaviest bone near the pointer; otherwise a stroke starts on a skinned mesh. */
export const paintWeightsPointer: MeshToolPointer = {
  press(ctx, p) {
    brushCursor.at = [p.sx, p.sy];
    const s = p.button === 0 ? meshScene(ctx) : null;
    const rows = s === null ? null : weightRows.of(s.target.mesh);
    if (p.button === 0 && rows === null) ctx.notify({ kind: 'info', key: s === null ? 'tools.editMesh.noMesh' : 'tools.paintWeights.rigid' });
    if (s === null || rows === null) return null;
    const at = brushAt(s, p);
    if (p.alt) {
      const bone = weightRows.boneAt(rows, s.points, at.x, at.y, at.radius);
      if (bone !== null) brushStore.setState({ bone });
      return null;
    }
    return startStroke(s, rows, at);
  },
  idle(ctx) {
    const s = meshScene(ctx);
    const rows = s === null ? null : weightRows.of(s.target.mesh);
    const bone = paintBoneOf(s);
    return s === null || rows === null || bone === null ? null : heatGizmo(s, rows, bone);
  },
  overlay(ctx: ToolContext): OverlayOptions {
    const s = meshScene(ctx);
    const bone = paintBoneOf(s);
    const name = bone === null ? undefined : ctx.doc.bones[bone]?.name;
    return s === null || name === undefined ? {} : { weights: name, selection: { slots: [s.at.slot] } };
  },
  hover(_ctx, p) {
    brushCursor.at = p === null ? null : [p.sx, p.sy];
  },
};
